import { Habit, SortingMode } from '@griply/types';
import { getWeekStartsOn, isHabitEnded } from '@griply/utils';
import { useCreateHabitAllowed, useHabits } from 'features/tasks';
import { useUpdateHabitOrder, useUserHadTrial } from 'features/user';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PremiumBanner } from 'shared/components/ui/premium-banner';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';

import { useWeekDate } from '../../contexts/timeframe';
import { SortableHabitsList } from '../sortable-habits-list';
import * as Styled from './no-grouping-habits-list.style';

export const NoGroupingHabitsList: React.FC = () => {
  const { t } = useTranslation();
  const today = useToday();
  const user = useUser();
  const habits = useHabits({
    uncompletedOnly: true,
    unArchivedOnly: true,
  });
  const weekDate = useWeekDate();

  const updateOrder = useUpdateHabitOrder();
  const openTaskDetail = useOpenTaskDetail();

  const {
    allowed: createHabitsAllowed,
    enforced: createHabitLimitsEnforced,
    limit: createHabitLimits,
    total: totalHabits,
  } = useCreateHabitAllowed();
  const openPremiumDialog = useOpenPremiumDialog();
  const userHadTrial = useUserHadTrial();

  const onHabit = useCallback(
    (habit: Habit) => {
      if (!habit.frozenAt) {
        openTaskDetail(habit);
      }
    },
    [openTaskDetail],
  );

  const activeHabits = useMemo(
    () =>
      getSortedTasks(
        habits.filter(
          (habit) => !habit.frozenAt && !isHabitEnded(habit.schedules, today),
        ),
        {
          today,
          weekStartsOn: getWeekStartsOn(user),
          mode: [user?.settings?.habitSortingMode].filter(
            Boolean,
          ) as unknown as SortingMode[],
          order: user?.settings?.habitSorting ?? [],
        },
      ) as Habit[],
    [habits, today, user],
  );

  const frozenHabits = useMemo(
    () =>
      getSortedTasks(
        habits.filter(
          (habit) => !!habit.frozenAt && !isHabitEnded(habit.schedules, today),
        ),
        {
          today,
          weekStartsOn: getWeekStartsOn(user),
          mode: [user?.settings?.habitSortingMode].filter(
            Boolean,
          ) as unknown as SortingMode[],
          order: user?.settings?.habitSorting ?? [],
        },
      ) as Habit[],
    [habits, today, user],
  );

  const showPremiumBanner =
    !createHabitsAllowed ||
    (!createHabitLimitsEnforced && createHabitLimits < totalHabits);

  return (
    <>
      <Styled.HabitsListContainer>
        <SortableHabitsList
          onReorder={updateOrder}
          habits={activeHabits}
          onHabit={onHabit}
          weekDate={weekDate}
        />
      </Styled.HabitsListContainer>

      {showPremiumBanner && (
        <PremiumBanner
          title={t(
            createHabitLimitsEnforced
              ? 'habits.premium-banner.title'
              : 'habits.premium-banner.not-enforced.title',
            { total: totalHabits, limit: createHabitLimits },
          )}
          description={t(
            createHabitLimitsEnforced
              ? userHadTrial
                ? 'habits.premium-banner.description'
                : 'pages.habits.premium-banner.try-for-free.description'
              : 'habits.premium-banner.not-enforced.description',
            { limit: createHabitLimits },
          )}
          onClick={openPremiumDialog}
          isTrial={!userHadTrial}
        />
      )}

      {!!frozenHabits.length && (
        <Styled.HabitsListContainer>
          <SortableHabitsList
            habits={frozenHabits}
            onHabit={openPremiumDialog}
            weekDate={weekDate}
          />
        </Styled.HabitsListContainer>
      )}
    </>
  );
};
